import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmptyRow, LoadingRow } from "@/components/common/States";
import { PaymentsTable } from "@/pages/Collections";
import { InstrumentsTable } from "@/pages/Instruments";
import { PromiseFilterChips, PromisesTable, filterParams } from "@/pages/Promises";

const Locked = ({ testid }) => (
  <div className="panel p-10 text-center text-slate-400" data-testid={testid}><Inbox className="h-6 w-6 mx-auto mb-2" />Bu sekmeyi görüntüleme yetkiniz yok</div>
);

function useList(url, params, enabled, reload) {
  const [res, setRes] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => { if (enabled) { setRes(null); api.get(url, { params: JSON.parse(key) }).then(({ data }) => setRes(data)); } }, [url, key, enabled, reload]);
  return res;
}

export function OpenInvoicesTab({ customerId, reload }) {
  const res = useList(`/customers/${customerId}/open-invoices`, {}, true, reload);
  return (
    <div className="panel overflow-hidden" data-testid="open-invoices-tab">
      <div className="flex flex-wrap gap-6 px-3 py-2 border-b border-slate-200 text-[13px]">
        <span>Açık kalem toplamı <b className="font-mono" data-testid="open-invoices-total">{formatTRY(res?.total_open)}</b></span>
        <span>Vadesi geçmiş <b className="font-mono text-rose-600" data-testid="open-invoices-overdue">{formatTRY(res?.overdue_total)}</b></span>
        {res?.unallocated_credit > 0 && <span>Eşleşmemiş alacak <b className="font-mono text-emerald-700">{formatTRY(res.unallocated_credit)}</b></span>}
      </div>
      <div className="overflow-x-auto">
        <table className="erp-table">
          <thead><tr><th>Belge No</th><th>Tarih</th><th>Vade</th><th>Açıklama</th><th className="text-right">Tutar</th><th className="text-right">Ödenen</th><th className="text-right">Kalan</th><th>Gecikme</th></tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={8} /> : res.items.length === 0 ? <EmptyRow cols={8} text="Açık fatura yok" /> : res.items.map((i) => (
              <tr key={i.id} data-testid={`open-invoice-${i.doc_no}`}>
                <td className="font-mono text-xs font-semibold">{i.doc_no}</td><td className="font-mono text-xs">{formatIsoDay(i.date)}</td>
                <td className={`font-mono text-xs ${i.days_overdue > 0 ? "text-rose-600 font-semibold" : ""}`}>{formatIsoDay(i.due_date)}</td>
                <td className="max-w-[260px] truncate" title={i.description}>{i.description}</td>
                <td className="text-right font-mono">{formatTRY(i.debit)}</td><td className="text-right font-mono text-emerald-700">{i.allocated ? formatTRY(i.allocated) : ""}</td>
                <td className="text-right font-mono font-semibold">{formatTRY(i.remaining)}</td>
                <td className="text-xs">{i.days_overdue > 0 ? <span className="text-rose-600">{i.days_overdue} gün</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CustomerPaymentsTab({ customerId, reload }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const res = useList("/tahsilatlar", { customer_id: customerId, page_size: 100 }, can("tahsilatlar"), reload);
  if (!can("tahsilatlar")) return <Locked testid="customer-payments-forbidden" />;
  return <div className="panel overflow-x-auto" data-testid="customer-payments-tab"><PaymentsTable res={res} showCustomer={false} onOpen={(id) => navigate(`/tahsilatlar?id=${id}`)} /></div>;
}

export function CustomerInstrumentsTab({ customerId, kind, reload }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const res = useList("/cek-senet", { customer_id: customerId, kind, page_size: 100 }, can("cek_senet"), reload);
  if (!can("cek_senet")) return <Locked testid={`customer-${kind}-forbidden`} />;
  return (
    <div className="panel overflow-x-auto" data-testid={`customer-${kind}-tab`}>
      {res && <div className="px-3 py-2 border-b border-slate-200 text-[13px]">Açık risk (portföy + banka + ciro) <b className="font-mono text-blue-700">{formatTRY(res.open_total)}</b></div>}
      <InstrumentsTable res={res} showCustomer={false} onOpen={(id) => navigate(`/cek-senet?tur=${kind}&id=${id}`)} />
    </div>
  );
}

export function CustomerPromisesTab({ customerId, reload, onChanged }) {
  const { can } = useAuth();
  const [filter, setFilter] = useState("");
  const res = useList("/odeme-sozleri", { customer_id: customerId, page_size: 100, ...filterParams(filter) }, can("tahsilatlar"), reload);
  if (!can("tahsilatlar")) return <Locked testid="customer-promises-forbidden" />;
  return (
    <div className="panel overflow-x-auto" data-testid="customer-promises-tab">
      <div className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <PromiseFilterChips summary={res?.summary} value={filter} onChange={setFilter} prefix="customer-promises-filter" />
        {res?.summary?.acik && <span className="text-[12px] text-slate-600" data-testid="customer-promises-open-total">Açık söz kalan <b className="font-mono">{formatTRY(res.summary.acik.amount)}</b> · {res.summary.acik.count} söz</span>}
      </div>
      <PromisesTable res={res} showCustomer={false} onChanged={onChanged} />
    </div>
  );
}
